import React, { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { Toaster } from '@/components/ui/sonner';
import { WalletProvider } from './contexts/WalletContext';
import Header from './components/Header';
import Hero from './components/Hero';
import SwapInterface from './components/SwapInterface';
import UnifiedWalletViewer from './components/UnifiedWalletViewer';
import RavencoinTrading from './components/RavencoinTrading';
import PublicAnalyticsDashboard from './components/PublicAnalyticsDashboard';
import CommunityMetricsView from './components/CommunityMetricsView';
import Footer from './components/Footer';
import TemporaryAdminBanner from './components/TemporaryAdminBanner';
import WalletReconnectionPrompt from './components/WalletReconnectionPrompt';
import { useIsCallerAdmin, useIsCallerTemporaryAdmin, useTrackVisitor } from './hooks/useQueries';
import { useActor } from './hooks/useActor';
import { useWallet } from './contexts/WalletContext';
import { Loader2 } from 'lucide-react';

// ============================================================================
// CODE SPLITTING - Only split admin components (rarely used)
// Main app components load immediately for smooth scrolling
// Wallet SDKs are split via Vite config (manualChunks)
// ============================================================================

// Only lazy load admin dashboards (not commonly used)
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const ValidationDashboard = lazy(() => import('./components/ValidationDashboard'));

// Loading fallback for admin components only
const LoadingFallback = () => (
  <div className="flex items-center justify-center py-12">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // Disable refetch on window focus to reduce CPU
      refetchOnMount: false, // Disable automatic refetch on mount - only refetch when explicitly needed
      refetchOnReconnect: false, // Disable automatic refetch on reconnect - reduce CPU usage
      retry: (failureCount, error: any) => {
        // Don't retry on 400 errors (wrong canister ID, etc.)
        if (error?.message?.includes('does not belong to any subnet') || 
            error?.message?.includes('400') ||
            error?.code === 400) {
          return false;
        }
        // Retry up to 1 time for other errors (reduced from 2)
        return failureCount < 1;
      },
      staleTime: 60000, // Consider data fresh for 60 seconds (increased from 30) to reduce queries
      refetchInterval: false, // Disable auto-refetch intervals by default (queries can override)
      refetchIntervalInBackground: false,
      gcTime: 300000, // Keep unused data in cache for 5 minutes (reduces refetches)
      // Note: onError removed - React Query v5 doesn't support it in defaultOptions
      // Errors should be handled in individual components using error states
    },
    mutations: {
      // Note: onError removed - React Query v5 doesn't support it in defaultOptions
      // Each mutation should handle its own errors explicitly using error states
    },
  },
});

function AppContent() {
  // QR code library will be loaded on-demand when DepositAddressModal opens
  // Removed preload to avoid initialization issues

  const { actor, isFetching: actorFetching } = useActor();
  const { data: isAdmin, isFetched: isAdminFetched, error: adminError } = useIsCallerAdmin();
  const { data: isTemporaryAdmin, isFetched: isTempAdminFetched, error: tempAdminError } = useIsCallerTemporaryAdmin();
  const { showReconnectionPrompt, isConnected, walletAddress } = useWallet();
  const trackVisitor = useTrackVisitor();

  const adminCheckComplete = !!actor && !actorFetching && isAdminFetched && isTempAdminFetched;
  // Show admin dashboard if:
  // - For wallet-based admin: requires wallet connection and address
  // - For temporary admin: only requires temporary admin status (no wallet needed)
  const showAdminDashboard = adminCheckComplete && (
    (isAdmin === true && isConnected && !!walletAddress) || 
    (isTemporaryAdmin === true)
  );
  
  // Admin status tracking (removed debug log for production)
  
  // Auto-scroll to admin dashboard when admin status becomes true
  React.useEffect(() => {
    if (showAdminDashboard) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const adminSection = document.getElementById('admin-dashboard-section');
        if (adminSection) {
          adminSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, [showAdminDashboard]);

  // Track visitor on page load (only once per day to save cycles)
  React.useEffect(() => {
    if (actor && !actorFetching) {
      // Only track once per day per user (saves ~99% of cycle costs)
      const lastTracked = localStorage.getItem('lastVisitorTrack');
      const today = new Date().toDateString();
      
      if (lastTracked !== today) {
        trackVisitor.mutate();
        localStorage.setItem('lastVisitorTrack', today);
      }
    }
  }, [actor, actorFetching]); // Only track once per day when actor becomes available
  
  // Log errors for debugging
  if (adminError || tempAdminError) {
    console.error('Admin query errors:', { adminError, tempAdminError });
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TemporaryAdminBanner />
      {showReconnectionPrompt && <WalletReconnectionPrompt />}
      <Header />
      <main>
        <Hero />
        <SwapInterface />
        <section id="wallet-viewer-section">
          <UnifiedWalletViewer />
        </section>
        <section id="ravencoin-orderbook-section">
          <RavencoinTrading />
        </section>
        <section id="analytics-section">
          <PublicAnalyticsDashboard />
          <CommunityMetricsView />
        </section>
        
        {/* Only admin components are lazy-loaded (rarely used) */}
        {showAdminDashboard && (
          <Suspense fallback={<LoadingFallback />}>
            <section id="admin-dashboard-section">
              <AdminDashboard />
            </section>
            <section id="validation-dashboard-section">
              <ValidationDashboard />
            </section>
          </Suspense>
        )}
      </main>
      <Footer />
      <Toaster />
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <WalletProvider>
          <AppContent />
        </WalletProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
